import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import ActionBarItemWrapper from '../items/ActionBarItemWrapper';
import { Props as ActionBarItemWrapperProps } from '../items/ActionBarItemWrapper/ActionBarItemWrapper';
import { Props as ActionBarButtonProps } from '../items/ActionBarButton';
import SubscribeToUnlockTooltip from '../SubscribeToUnlockTooltip';

export interface Props extends ActionBarItemWrapperProps {
  button: React.ReactElement<ActionBarButtonProps>;
  /** If true and user is anon, shows a 'need to log in' tooltip
   *  and sends the user to sisu if clicked. */
  loggedInOnly?: boolean;
  /** If true and user is unsubscribed, shows a 'need to subscribe' tooltip
   *  and sends the user to tier purchase page if clicked. */
  subscribersOnly?: boolean;
  /** A Bi referral type to indicate what action is being attempted/blocked
   *  see: @wix/da-bi/pkg/constants, e.g. ReferralType.ANON_DOWNLOAD */
  restrictedActionName?: ReferralType;
  /** Force showing the tooltip regardless of hover/click */
  forceVisibleTooltip?: boolean;
  deviation: PapiDeviation;
}

/** Blocks an action/click on the given action bar button if:
 *  - loggedInOnly: sends the clicking user to sisu if anon
 *  - subscribersOnly: sends the clicking user to purchase the tier if unsubbed or,
 *    if locked-subscribed, just blocks the action
 *  Accepts the same props as ActionBarWrapper, allowing popups to work as normal.
 */
const ActionBarButtonRestricted: React.FC<React.PropsWithChildren<Props>> = ({
  button,
  loggedInOnly,
  subscribersOnly,
  deviation,
  restrictedActionName,
  tooltip,
  forceVisibleTooltip,
  renderPopover,
  notificationTypes,
}) => {
  const { t } = useTranslation();

  const { isLoggedIn, restrictToLoggedIn } = useRestrictToLoggedIn();
  const { needsSubscription, isSubscribedButBlocked } =
    useSubscriptionTier(deviation);

  if (loggedInOnly && !isLoggedIn) {
    return (
      <ActionBarItemWrapper
        tooltip={t('actionbar.download.mustLogIn')}
        forceVisibleTooltip={forceVisibleTooltip}
        notificationTypes={notificationTypes}
      >
        {cloneWithUpdateClickBehaviour(button, {
          onClick: restrictedActionName
            ? restrictToLoggedIn(restrictedActionName)
            : undefined,
          href: undefined,
        })}
      </ActionBarItemWrapper>
    );
  }

  const actionBlockedBySubscription =
    needsSubscription || isSubscribedButBlocked;
  if (subscribersOnly && actionBlockedBySubscription) {
    return (
      <SubscribeToUnlockTooltip
        deviation={deviation}
        forceVisible={forceVisibleTooltip}
      >
        {cloneWithUpdateClickBehaviour(button, {
          onClick: undefined,
          href: undefined,
        })}
      </SubscribeToUnlockTooltip>
    );
  }

  return (
    <ActionBarItemWrapper
      tooltip={tooltip}
      renderPopover={renderPopover}
      forceVisibleTooltip={forceVisibleTooltip}
      notificationTypes={notificationTypes}
    >
      {button}
    </ActionBarItemWrapper>
  );
};
ActionBarButtonRestricted.displayName = 'ActionBarButtonRestricted';
export default ActionBarButtonRestricted;

function cloneWithUpdateClickBehaviour(
  button: React.ReactElement,
  newProps
): React.ReactElement {
  return React.cloneElement(
    button,
    {
      ...(button as any).props,
      download: undefined,
      target: undefined,
      ...newProps,
    },
    (button as any).props.children
  );
}
